/**
 * columnUtils.js
 *
 * Utility functions and constants for managing columns in a dynamic table-like interface.
 * This module includes enumerations for different column types and standard column IDs,
 * as well as mappings between column types and their corresponding UI icons. It also provides
 * a function for generating the default column configuration with translated labels and fixed widths.
 *
 * Key exports:
 * - `ColumnTypes`: Enumeration of column types (e.g., STRING, DATE, STATUS).
 * - `StandardColumnIds`: Enumeration of standard column IDs (e.g., TASK_NAME, START_DATE).
 * - `columnTypeToIcon`: Mapping of column types to corresponding icons.
 * - `createStandardColumns`: Function to create standard columns with translated labels and fixed widths.
 */

import AbcIcon from '@mui/icons-material/Abc';
import CalculateIcon from '@mui/icons-material/Calculate';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ScheduleIcon from '@mui/icons-material/Schedule';

/**
 * Task column types enumeration.
 * 
 * These values represent different column types and are used to map column data types.
 * These values are saved in the backend and must not be changed.
 * 
 * @readonly
 * @enum {number}
 */
const ColumnTypes = Object.freeze({
  /**
   * String type column (e.g., task name)
   */
  STRING: 0,
  /**
   * Number type column (e.g., numeric values)
   */
  NUMBER: 1,
  /**
   * Date type column (e.g., start or end date)
   */
  DATE: 2,
  /**
   * Status type column (e.g., task completion status)
   */
  STATUS: 3,
  /**
   * Time report (Duration) type column
   */
  TIME_REPORT: 4,
});

/**
 * Standard task column IDs enumeration.
 * 
 * These values are used as unique identifiers for columns and are stored in the backend.
 * These IDs should not be changed.
 * 
 * @readonly
 * @enum {string}
 */
const StandardColumnIds = Object.freeze({
  /**
   * Task name column
   */
  TASK_NAME: "_task",
  /**
   * Start date column
   */
  START_DATE: "_startDate",
  /**
   * End date column
   */
  END_DATE: "_endDate",
  /**
   * Assigned to column
   */
  ASSIGNED_TO: "_assigned",
  /**
   * Time report (Duration) column
   */
  TIME_REPORT: "_reportTime",
});

/**
 * Maps each column data type to its corresponding icon component.
 * 
 * This object allows dynamic rendering of icons in the UI based on the column's data type.
 * The keys represent different column types (e.g., date, string, number), and the values are
 * the corresponding icon components that should be displayed for each type.
 * 
 * @type {Object.<ColumnTypes, React.Component>}
 */
const columnTypeToIcon = {
  [ColumnTypes.DATE]: CalendarMonthIcon,
  [ColumnTypes.NUMBER]: CalculateIcon,
  [ColumnTypes.STATUS]: DoneAllIcon,
  [ColumnTypes.STRING]: AbcIcon,
  [ColumnTypes.TIME_REPORT]: ScheduleIcon,
};

/**
 * Creates the standard column configuration with translations and column widths.
 * 
 * This function generates the default set of columns for the project table, including
 * column labels (translated) and fixed column widths. The widths ensure a consistent 
 * layout for each column in the UI.
 * If the `intl` object (for translations) is provided, it will be used to get the column labels.
 * If `intl` is not available, fallback strings are used.
 * 
 * @param {Object} [intl=undefined] - The `intl` object used for translations (optional).
 * @returns {Array} The array of standard column configurations, each containing:
 *   - `fieldId` {string} - The unique identifier for the column.
 *   - `label` {string} - The label text for the column (translated or fallback).
 *   - `width` {string} - The width configuration for the column.
 */
const createStandardColumns = (intl = undefined) => [
  {
    fieldId: StandardColumnIds.TASK_NAME,
    label: intl?.formatMessage({ id: "project.standardColumn.task" }) || "Task name",
    width: "minmax(450px, 1fr)",
  },
  {
    fieldId: StandardColumnIds.START_DATE,
    label: intl?.formatMessage({ id: "project.standardColumn.startDate" }) || "Start date",
    width: "120px",
  },
  {
    fieldId: StandardColumnIds.END_DATE,
    label: intl?.formatMessage({ id: "project.standardColumn.endDate" }) || "End date",
    width: "120px",
  },
  {
    fieldId: StandardColumnIds.ASSIGNED_TO,
    label: intl?.formatMessage({ id: "project.standardColumn.assigned" }) || "Assigned to",
    width: "200px",
  },
  {
    fieldId: StandardColumnIds.TIME_REPORT,
    label: intl?.formatMessage({ id: "project.standardColumn.reportedTime" }) || "Reported time",
    width: "120px",
  },
];

export {
  columnTypeToIcon,
  createStandardColumns,
  ColumnTypes,
  StandardColumnIds,
};
